import {
  CAB_FACILITY_ICON_CLASS,
  CAB_FACILITY_TYPE,
  LOBS_CONSTANTS,
} from '../constants/packageConstants';
import { formatDateFromStr } from './formattingUtils';

const sightSeeingMapping = {};

const getTransferFacilities = transfer => {
  let facilities = [];
  if (transfer.vehicleMaxPax) {
    facilities.push(`${transfer.vehicleMaxPax} Seat${transfer.vehicleMaxPax > 1 ? 's' : ''}`);
  }
  facilities.push(transfer.vehicleCategory?.replace('NON_', 'Non-'));
  facilities = facilities.join(' | ');
  return facilities;
};

const getTransferSuitableFor = transfer => {
  const suitableFor = [];
  for (const index in transfer.suitableForPaxType) {
    const category = transfer.suitableForPaxType[index];
    suitableFor.push(category.charAt(0).toUpperCase() + category.slice(1).toLowerCase());
  }
  return suitableFor.join(', ');
};

const getTransferTypeText = type => {
  let text = '';
  switch (type) {
    case 'PRIVATE':
      text = 'Private Transfer';
      break;
    case 'SHARED':
      text = 'Shared Transfer';
      break;
    case 'GROUP':
      text = 'Shared Transfer';
      break;
    case 'NONE':
      text = 'No Transfer';
      break;
    default:
      break;
  }

  return text;
};

const getFacilityIcon = type => {
  let text = CAB_FACILITY_ICON_CLASS.DEFAULT;
  switch (type) {
    case CAB_FACILITY_TYPE.AC:
      text = CAB_FACILITY_ICON_CLASS.AC;
      break;
    case CAB_FACILITY_TYPE.CARRIER:
      text = CAB_FACILITY_ICON_CLASS.CARRIER;
      break;
    case CAB_FACILITY_TYPE.FIRSTAID:
      text = CAB_FACILITY_ICON_CLASS.FIRSTAID;
      break;
    case CAB_FACILITY_TYPE.LUGGAGE:
      text = CAB_FACILITY_ICON_CLASS.LUGGAGE;
      break;
    case CAB_FACILITY_TYPE.SEATER:
      text = CAB_FACILITY_ICON_CLASS.SEATER;
      break;
    default:
      break;
  }

  return text;
};
const getTransferType = transfer => {
  if (transfer?.defaultSelection) {
    return getTransferTypeText(transfer.defaultSelection);
  }
  if (transfer?.vehicleInfo?.privateOrShared) {
    return getTransferTypeText(transfer.vehicleInfo.privateOrShared);
  }
  return '';
};

const getFormattedSimilarTransfers = (similarTransfers, lobData) => {
  const { type, sellableId, transferSequence, defaultSelection } = lobData;
  if (type === LOBS_CONSTANTS.AIRPORT_TRANSFER) {
    let availableOptions = [];
    const { transfersListingData } = similarTransfers;
    const { airportTransferChangeList, discountedFactor: df } = transfersListingData;
    const discountedFactor = typeof df === 'number' ? df : 1;

    const similarTransfersForCurrentTransferSequence =
      airportTransferChangeList &&
      airportTransferChangeList.find(transfer => transfer.transferSequence === transferSequence);
    if (!similarTransfersForCurrentTransferSequence) {
      return [];
    }
    availableOptions =
      (similarTransfersForCurrentTransferSequence &&
        Object.keys(similarTransfersForCurrentTransferSequence.priceMap).filter(
          transferType => transferType !== defaultSelection && transferType !== 'NONE'
        )) ||
      [];
    availableOptions.push(
      Object.keys(similarTransfersForCurrentTransferSequence.priceMap).filter(
        transferType => transferType === defaultSelection
      )[0]
    );
    availableOptions =
      availableOptions &&
      availableOptions.map(option => ({
        transferType: getTransferTypeText(option),
        type: option,
        addPackagePrice: similarTransfersForCurrentTransferSequence?.priceMap[option],
        removePackagePrice: similarTransfersForCurrentTransferSequence?.priceMap.NONE,
        facilities:
          (similarTransfersForCurrentTransferSequence?.vehicleInfoMap &&
            similarTransfersForCurrentTransferSequence?.vehicleInfoMap[option]?.facilities) ||
          [],
        model:
          (similarTransfersForCurrentTransferSequence?.vehicleInfoMap &&
            similarTransfersForCurrentTransferSequence?.vehicleInfoMap[option]?.model) ||
          '',
        imageUrls:
          (similarTransfersForCurrentTransferSequence?.vehicleInfoMap && [
            similarTransfersForCurrentTransferSequence?.vehicleInfoMap[option]?.imageUrl,
          ]) ||
          [],
        vehicleName:
          (similarTransfersForCurrentTransferSequence?.vehicleInfoMap &&
            similarTransfersForCurrentTransferSequence?.vehicleInfoMap[option]?.vehicleName) ||
          '',
        discountedFactor,
      }));
    return availableOptions || [];
  }
  if (type === LOBS_CONSTANTS.CAR_ITINERARY || type === LOBS_CONSTANTS.COMMUTE) {
    const { carListingData, priceDiffMap } = similarTransfers;
    const {
      commuteDetail,
      packagePriceMap = {},
      removedPriceMap = {},
      inclusionText,
      discountedFactor: df,
    } = carListingData;
    const discountedFactor = typeof df === 'number' ? df : 1;

    return commuteDetail.map(option => {
      const inclusions =
        typeof option?.additionalInfo?.inclusions === 'string'
          ? [inclusions]
          : option?.additionalInfo?.inclusions;
      return {
        transferType: getTransferTypeText(option?.vehicleInfo?.privateOrShared),
        description: option?.additionalData?.shortDescription,
        inclusion: option?.additionalInfo?.inclusion,
        exclusion: option?.additionalInfo?.exclusion,
        vehicleName: option?.vehicleInfo?.vehicleName,
        facilities: option?.vehicleInfo?.facilities,
        imageUrls: [option?.vehicleInfo?.imageUrl],
        model: option?.vehicleInfo?.model,
        importantInfos: option?.additionalInfo?.importantInfos,
        inclusionText,
        inclusions: inclusions || [],
        exclusions: option?.additionalInfo?.exclusions || [],
        resultSellableId: option?.sellableId,
        safe: option?.safe,
        safetyRating: option?.safetyRating,
        addPackagePrice: packagePriceMap[option?.sellableId],
        removePackagePrice: removedPriceMap[option?.sellableId],
        priceDifference: priceDiffMap && priceDiffMap[option?.sellableId],
        discountedFactor,
      };
    });
  }
  return [];
};

const getFormattedTransferDetails = (details, lobData) => {
  const transfer = {
    type: 'COMMUTE',
    addPackagePrice: details?.addPackagePrice,
    discountedFactor: details?.discountedFactor,
    removePackagePrice: details?.removePackagePrice,
    transferSequence: lobData?.transferSequence,
    defaultSelection: lobData?.defaultSelection,
    transferType: getTransferType(lobData),
    description: lobData?.additionalInfo?.description || lobData?.additionalData?.description,
    inclusions: lobData?.additionalInfo?.inclusions || lobData?.additionalData?.inclusions || [],
    exclusions: lobData?.additionalInfo?.exclusions || lobData?.additionalData?.exclusions || [],
    importantInfos: lobData?.additionalInfo?.importantInfos,
    driverSpokenLanguages: lobData?.vehicleInfo?.driverSpokenLanguages,
    facilities: lobData?.vehicleInfo?.facilities,
    imageUrls: [lobData?.vehicleInfo?.imageUrl],
    model: lobData?.vehicleInfo?.model,
    sellableId: lobData?.sellableId,
    resultSellableId: details?.resultSellableId,
    vehicleName: lobData?.vehicleInfo?.vehicleName,
    vehicleMaxPax: lobData?.vehicleInfo?.maxPaxCapacity,
    vehicleCategory: lobData?.vehicleInfo?.vehicleCategory,
    duration: lobData?.metaData?.duration,
    transferCode: lobData?.metaData?.code,
    isLocked: lobData?.metaData?.locked,
    inclusionText: lobData?.additionalData?.inclusionText,
    startingPoint: lobData?.additionalInfo?.pickUpPoint || lobData?.additionalData?.pickUpPoint,
    endingPoint: lobData?.additionalInfo?.dropOffPoint || lobData?.additionalData?.dropOffPoint,
    suitableForPaxType: lobData?.metaData?.suitableForPaxType,
    safe: lobData?.safe || lobData?.additionalData?.safe,
    safetyRating: lobData?.safetyRating || lobData?.additionalData?.safetyRating,
    commuteCode: lobData?.commuteCode,
    priceDifference: details?.priceDifference,
  };
  return transfer;
};
const getSightSeeingOptions = carItineraryDetail => {
  if (Object.keys(sightSeeingMapping).length !== 0) {
    return sightSeeingMapping;
  }
  if (carItineraryDetail?.carContents) {
    const carItineraryDestinations = carItineraryDetail?.carContents[0]?.carDayWiseContents || [];
    if (carItineraryDestinations.length) {
      carItineraryDestinations.map(options => {
        options?.carSightseeingDetails.map(sightSeeingOption => {
          const { cityName, name } = sightSeeingOption;
          if (cityName) {
            if (sightSeeingMapping.hasOwnProperty(cityName)) {
              sightSeeingMapping[`${cityName}`].push(name);
            } else {
              sightSeeingMapping[`${cityName}`] = [name];
            }
          }
        });
      });
    }
  }
  return sightSeeingMapping;
};
const getChangePrice = (
  currPackagePrice,
  addPackagePrice,
  discountedFactor = 1,
  removePackagePrice,
  isRemoveClicked
) => {
  const removePrice = (addPackagePrice - removePackagePrice) * discountedFactor;
  let priceShown = (currPackagePrice - addPackagePrice) * discountedFactor;
  let isNegative = true;
  if (addPackagePrice > currPackagePrice) {
    priceShown = (addPackagePrice - currPackagePrice) * discountedFactor;
    isNegative = false;
  }

  if (isRemoveClicked) {
    priceShown = removePrice;
    isNegative = false;
  }
  return {
    priceShown: Math.round(isNegative ? -Math.abs(priceShown) : priceShown),
    removePrice: Math.round(isNegative ? -Math.abs(removePrice) : removePrice),
  };
};
const getIncludedCarItineraryIdDayMap = dayItineraries => {
  const carItineraryDayMap = {};
  dayItineraries.forEach(dayItinerary => {
    dayItinerary.itineraryUnits.forEach(unit => {
      if (unit.itineraryUnitType === LOBS_CONSTANTS.CAR) {
        if (!carItineraryDayMap[unit.car.sellableId]) {
          carItineraryDayMap[unit.car.sellableId] = [];
        }
        carItineraryDayMap[unit.car.sellableId].push(dayItinerary.day);
      }
    });
  });

  return carItineraryDayMap;
};
const getIncludedSightseeingIdDayMap = content => {
  const sightseeingIdDayMap = {};
  const dayWiseSightseeing = getCarSightSeeings(content);
  dayWiseSightseeing.forEach(sightSeeing => {
    if (!sightseeingIdDayMap[sightSeeing.sellableId]) {
      sightseeingIdDayMap[sightSeeing.sellableId] = [];
    }
    sightseeingIdDayMap[sightSeeing.sellableId].push(sightSeeing.day);
  });

  return sightseeingIdDayMap;
};
const getCarSightSeeings = (content, destinationDetail) => {
  if (content?.carItinerary?.length) {
    let itineraries = [];
    content.carItinerary.forEach(itinerary => {
      const { sellableId } = itinerary;
      itineraries = itineraries.concat(
        itinerary.carDayWiseContents.reduce(
          (allSightSeeings, current) =>
            allSightSeeings.concat(
              current.carSightseeingDetails
                ? current.carSightseeingDetails.map(sightSeeing => ({
                    car: {
                      ...sightSeeing,
                      date: createDayDateMap(destinationDetail, current.dayNumber),
                    },
                  }))
                : []
            ),
          []
        )
      );
    });
    return itineraries;
  }
  return [];
};
const createDayDateMap = (destinationDetail, day) => {
  const dayDateMap = new Map();
  const startDate = destinationDetail.destinations[0].start;
  const endDate = destinationDetail.destinations[destinationDetail.destinations.length - 1].end;
  const datesArr = getAllDatesInRange(startDate, endDate);
  for (let i = 0; i < datesArr.length; i++) {
    dayDateMap.set(i + 1, Moment(datesArr[i]).format('YYYY-MM-DD'));
  }
  return dayDateMap.get(day);
};
const getAllDatesInRange = (startDate, endDate) => {
  const listDate = new Array();
  let currentDate = formatDateFromStr(startDate);
  const stopDate = formatDateFromStr(endDate);
  while (currentDate <= stopDate) {
    listDate.push(currentDate);
    currentDate = currentDate.addDays(1);
  }
  return listDate;
};
export default {
  getTransferFacilities,
  getTransferSuitableFor,
  getTransferType,
  getFormattedSimilarTransfers,
  getFormattedTransferDetails,
  getSightSeeingOptions,
  getChangePrice,
  getFacilityIcon,
  getIncludedCarItineraryIdDayMap,
  getIncludedSightseeingIdDayMap,
};
